import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  getFacTypes, 
  getFacilityTypes,
  getFacListByType,
  searchFacilities 
} from "../services/shared";

/**
 * Hook to manage facility data with seamless migration from old to new system
 * Tries new CSV-based API first, falls back to old system if needed
 */
export const useFacilityData = (country) => {
  // Try new CSV-based API first
  const {
    data: newData,
    isLoading: newLoading,
    error: newError,
    refetch: refetchNew
  } = useQuery(
    ['facTypes', country],
    () => getFacTypes(country),
    {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onError: (error) => {
        console.log('New facility API not available, will use legacy system');
      }
    }
  );

  // Fallback to old API if new one fails
  const {
    data: oldData,
    isLoading: oldLoading,
    error: oldError,
    refetch: refetchOld
  } = useQuery(
    ['facilityTypes', country],
    () => getFacilityTypes(country),
    {
      enabled: !!newError, // Only fetch if new API failed
      retry: 2,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  // Determine which data to use
  const isUsingNewSystem = !newError && newData?.data?.data;
  const facilityData = isUsingNewSystem 
    ? newData?.data?.data 
    : oldData?.data?.data;

  // Normalize data structure for consistent usage (memoized to prevent unnecessary re-renders)
  const normalizedData = useMemo(() => {
    return facilityData?.map(facility => {
      if (isUsingNewSystem) {
        // New system already has the right structure
        return facility;
      } else {
        // Old system needs normalization
        return {
          id: facility.id,
          name: facility.name,
          count: facility.types?.length || 0,
          types: facility.types?.map(type => ({
            id: type.id,
            fac_id: type.id, // For compatibility
            name: type.name,
            code: type.slug || `${facility.id}${type.id}`,
            sub_type: type.id.toString().slice(-2),
          })) || []
        };
      }
    });
  }, [facilityData, isUsingNewSystem]);

  return {
    data: normalizedData,
    isLoading: newLoading || (newError && oldLoading),
    error: newError && oldError,
    refetch: () => {
      if (isUsingNewSystem) {
        return refetchNew();
      } else {
        return refetchOld();
      }
    },
    isUsingNewSystem,
  };
};

/**
 * Hook to get facility subtypes for a specific type
 */
export const useFacilitySubtypes = (typeId, country, enabled = true) => {
  const { isUsingNewSystem } = useFacilityData(country);

  const { data, isLoading, error } = useQuery(
    ['facSubtypes', typeId, country],
    () => getFacListByType(typeId, country),
    {
      enabled: enabled && isUsingNewSystem && !!typeId,
      retry: 2,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  return {
    data: data?.data?.data,
    isLoading,
    error,
  };
};

/**
 * Hook to search facilities
 */
export const useFacilitySearch = (country, query, enabled = true) => {
  const { data, isLoading, error } = useQuery(
    ['facSearch', country, query],
    () => searchFacilities(country, query),
    {
      enabled: enabled && query?.length >= 2,
      retry: 1,
      staleTime: 30 * 1000, // 30 seconds
      cacheTime: 60 * 1000, // 1 minute
    }
  );

  return {
    data: data?.data?.data,
    isLoading,
    error,
  };
};

export default useFacilityData;